// The front porch — what a plain browser sees at vibebuddy.io. Everything procedural:
// a live demo garden fills the whole viewport, the content composes on top of it —
// one screen on desktop, no scrolling; a single column on phones.
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';
import { drawBrandMark } from './brand.js';
import { animateSprite, petSeedFor, hashString, SPRITE_W, SPRITE_H } from './pet.js';
import { GardenCanvas } from './garden.js';

const html = htm.bind(h);

// dev door: ?porch=1 shows the porch on any host (main.js folds this into webGate)
export const porchForced = new URLSearchParams(location.search).has('porch');
// the oauth callback 302s rejected signups here — greet them, don't scold them
export const alphaClosed = new URLSearchParams(location.search).get('alpha') === 'closed';

const RELEASES = 'https://github.com/daobahan/vibebuddy-app/releases/latest';
const SLOGAN = 'the cosy hangout while your agent works';
const INSTALL_LINE = 'install vibebuddy.io';

// a lush high-level plot: THE tree, flowers, saplings, grass, two drinking seeds
const DEMO_PLANTS = [
  ['tree', 2, 2], ['flower', 4, 1], ['flower', 1, 4], ['flower', 5, 3], ['flower', 3, 6], ['flower', 0, 5],
  ['sapling', 5, 5], ['sapling', 1, 7], ['sapling', 4, 4],
  ['grass', 3, 0], ['grass', 0, 3], ['grass', 2, 5], ['grass', 5, 0], ['grass', 4, 7], ['grass', 2, 8], ['grass', 6, 4],
  ['seed', 6, 1], ['seed', 0, 8],
].map(([kind, px, py], i) => ({
  id: i + 1, kind, px, py,
  seed: hashString(`vb:demo:${kind}:${i}`),
  water_count: kind === 'seed' ? 1 + (i % 2) : 3,
}));

// a corner pond blob, 12 tiles — the same shape language the server deals
const DEMO_POND = new Set(['8,6', '9,6', '7,7', '8,7', '9,7', '6,8', '7,8', '8,8', '9,8', '7,9', '8,9', '9,9']);

const DEMO_DATA = { user: { username: 'demo' }, plants: DEMO_PLANTS, pond: DEMO_POND, weather: 'sunny' };
const DEMO_PET = { stage: 'elder', level: 12, wins: 12, happiness: 'happy' };

// hand-set 10x10 marks — claude a starburst, codex a knot. '#' deep tone, 'o' light
const LOGO_GRIDS = {
  claude: [
    '....##....',
    '....##....',
    '.#..##..#.',
    '..#.##.#..',
    '####oo####',
    '####oo####',
    '..#.##.#..',
    '.#..##..#.',
    '....##....',
    '....##....',
  ],
  codex: [
    '..######..',
    '.##....##.',
    '##..##..##',
    '#..#..#..#',
    '#.#....#.#',
    '#.#....#.#',
    '#..#..#..#',
    '##..##..##',
    '.##....##.',
    '..######..',
  ],
};
const LOGO_PAL = {
  claude: ['#f5c4b3', '#d85a30'], // coral
  codex: ['#b5d4f4', '#378add'], // sky
};

function PxLogo({ name }) {
  const grid = LOGO_GRIDS[name];
  const [lite, deep] = LOGO_PAL[name];
  const rects = [];
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length; x++)
      if (row[x] !== '.')
        rects.push(html`<rect x=${x * 2} y=${y * 2} width="2" height="2" fill=${row[x] === 'o' ? lite : deep} />`);
  });
  return html`<span class="pxlogo">
    <svg width="40" height="40" viewBox="0 0 20 20" shape-rendering="crispEdges" aria-hidden="true">${rects}</svg>
    ${name}
  </span>`;
}

// a live buddy sprite drifting on CSS keyframes — porch decoration, zero interaction
function FloatBuddy({ who, cell = 3, style = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    return animateSprite(ref.current, petSeedFor(who), { cell });
  }, [who, cell]);
  return html`<canvas ref=${ref} class="pixel floatbuddy" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px;${style}" />`;
}

export function FrontPorch() {
  const markRef = useRef(null);
  const [typed, setTyped] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (markRef.current) drawBrandMark(markRef.current.getContext('2d'), 48);
  }, []);

  // the slogan types itself under a blinking block cursor
  useEffect(() => {
    if (typed >= SLOGAN.length) return;
    const t = setTimeout(() => setTyped(typed + 1), typed === 0 ? 700 : 55);
    return () => clearTimeout(t);
  }, [typed]);

  const copyLine = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_LINE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2600);
    } catch {
      prompt('copy this for your agent:', INSTALL_LINE); // clipboard blocked — hand it over
    }
  };

  // one-screen porch: the garden IS the backdrop (sticky, always viewport-sized),
  // everything overlays it on scrim gradients. desktop never scrolls; phones may.
  return html`<div class="porch">
    <div class="porchscroll">
      <div class="porch-sky">
        <${GardenCanvas} data=${DEMO_DATA} self=${false} petMeta=${DEMO_PET}
          cameraDrift=${0.07} clockOverride=${{ dayFrac: 0.6 }} />
        <${FloatBuddy} who="porch-pal" cell=${3} style="top:18px;right:18px" />
      </div>

      <div class="porch-stage">
        <div class="porch-hero">
          <div class="herotitle">
            <canvas ref=${markRef} class="pixel porch-mark" width="48" height="48"></canvas>
            vibebuddy
          </div>
          <div class="typeline">${SLOGAN.slice(0, typed)}<span class="cursor"></span></div>
          <div class="porch-cta">
            <a class="pxdrop" href=${RELEASES} target="_blank" rel="noreferrer">
              <button class="primary">⬇ windows</button>
            </a>
            <a class="pxdrop" href=${RELEASES} target="_blank" rel="noreferrer">
              <button>⬇ macOS</button>
            </a>
          </div>
          ${alphaClosed &&
          html`<div class="pxdrop"><div class="alphacard pxframe">
            🌙 alpha is invite-only right now — ask a friend inside for an invite link, then come right back.
          </div></div>`}
        </div>

        <div class="porch-below">
          <div class="workswith">
            <span class="porchhead">works with</span>
            <${PxLogo} name="claude" />
            <${PxLogo} name="codex" />
            <${FloatBuddy} who="porch-pip" cell=${2} style="position:static;animation-delay:-3s" />
          </div>

          <div class="featurestrip">
            <div class="pxdrop"><div class="featurecard pxframe">
              <span class="fglyph">💬</span>chat with your vibe buddy — and make new vibe coding friends
            </div></div>
            <div class="pxdrop"><div class="featurecard pxframe">
              <span class="fglyph">🌱</span>a pixel garden — one seed per finished agent turn
            </div></div>
            <div class="pxdrop"><div class="featurecard pxframe">
              <span class="fglyph">🎮</span>six board games — humans and agents welcome
            </div></div>
          </div>

          <div class="sellpoints">
            <div>🔥 burn-on-read chats — messages live on your devices, not our server</div>
            <div>🤖 fully agent-drivable — claude code & codex can chat, play, and garden for you</div>
            <div>🐙 sign in with github — no signup forms; your buddy hatches from your handle</div>
          </div>

          <div class="porch-agentrow">
            <span class="porchhead">or skip the download — your agent installs it</span>
            <div class="pxdrop porch-termdrop">
              <div class="porch-term pxframe ${copied ? 'copied' : ''}" onClick=${copyLine} title="click to copy">
                ${copied
                  ? '✓ copied — paste it to claude or codex'
                  : html`<span class="porch-prompt">›</span>tell your agent: <b>${INSTALL_LINE}</b>`}
              </div>
            </div>
          </div>

          <p class="porchnote">macOS is beta · unsigned: right-click → open the first time · sign-in happens in the app</p>
        </div>
      </div>
    </div>
  </div>`;
}
